import React, { useState, useMemo } from "react";
import { Link, Redirect } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowCircleLeft } from "@fortawesome/pro-regular-svg-icons/faArrowCircleLeft";
import { faFileCsv } from "@fortawesome/pro-regular-svg-icons/faFileCsv";
import { faUpload } from "@fortawesome/pro-regular-svg-icons/faUpload";
import { faTimes } from "@fortawesome/pro-regular-svg-icons/faTimes";
import { faHistory } from "@fortawesome/pro-regular-svg-icons/faHistory";
import UserImportList from "./UserImportList";

import { Helmet } from "react-helmet";
import { Field, getIn } from "formik";
import axios from "axios";
import { toast } from "react-toastify";
import isString from "lodash/isString";

import { Heading, Group, Form, Select, Error } from "@peracto/peracto-ui";
import { useConfig } from "@peracto/peracto-config";

export const EMPTY_CELL_OPTIONS = [
  { label: "Ignore Empty Cells", value: "ignoreEmptyCells" },
  { label: "Delete Existing Values", value: "removeExistingValues" },
];

export const IMPORT_TYPES = [
  { label: "Users", value: "user" },
  { label: "Babbage Accounts", value: "babbage_account" },
];

const UserImport = () => {
  const config = useConfig();
  const API_URL = config.get("api");
  const [selectedFile, setSelectedFile] = useState();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showImportHistory, setShowImportHistory] = useState(false);
  const [missingProperties, setMissingProperties] = useState();
  const [unrecognisedColumns, setUnrecognisedColumns] = useState();
  const [redirect, setRedirect] = useState();

  const importTypes = [...IMPORT_TYPES];

  const handleSubmit = async ({ importType, file }, actions) => {
    if (!file) return;

    setIsSubmitting(true);
    setMissingProperties();
    setUnrecognisedColumns();

    const reader = new FileReader();

    reader.readAsDataURL(file);

    const formData = new FormData();
    formData.append("file", file);
    formData.append("importType", importType);
    formData.append("sourceType", "csv");

    const headers = {
      "Content-Type": "multipart/form-data",
      Authorization: `Bearer ${localStorage.getItem("token")}`,
    };

    try {
      const response = await axios.post(`${API_URL}/imports`, formData, {
        headers,
      });

      if (response?.data?.errors?.length > 0) {
        console.error("error:", response);
        const errorMessages = response?.data?.errors.map((err) => err.message);
        toast.error(errorMessages.join("\n"));

        const invalidColumnData = response.data.errors.find(
          (err) => err.type === "invalid_columns"
        );

        if (invalidColumnData?.extra_data?.missing_columns?.length > 0) {
          setMissingProperties(
            invalidColumnData.extra_data.missing_columns.join(", ")
          );
        }

        if (invalidColumnData?.extra_data?.unrecognised_columns?.length > 0) {
          setUnrecognisedColumns(
            invalidColumnData.extra_data.unrecognised_columns.join(", ")
          );
        }
      } else {
        console.log("success:", response);
        toast.success(
          "User data successfully uploaded, the import will run shortly."
        );
        setRedirect(`/users/import/${response.data.id}`);
      }
    } catch (e) {
      const { response } = e;
      console.error("error:", response);
      if (response?.data?.violations?.length > 0) {
        // Display errors for invalid fields
        response.data.violations.map((error) => {
          return actions.setFieldError(error.propertyPath, error.message);
        });
      }
      toast.error(
        e?.error?.body?.hasOwnProperty("hydra:description")
          ? e.error.body["hydra:description"]
          : "Whoops, there was a problem..."
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      {redirect && <Redirect to={redirect} />}
      <Helmet>
        <title>Bulk Manage Data | Peracto</title>
      </Helmet>
      <div className="container-wide">
        <Heading name="Bulk Manage Data">
          <div className="ml-2 d-flex align-items-center">
            <Link className="btn btn-outline-primary" to="/users">
              <FontAwesomeIcon icon={faArrowCircleLeft} className="mr-2" />
              Back to Users
            </Link>
          </div>
        </Heading>

        <Group key="import-data" id="import-data" name="Data Upload">
          <Form
            values={{
              importType: "user",
              file: "",
              emptyCellHandling: "ignoreEmptyCells",
              removeExistingValues: false,
            }}
            showActionPanel={false}
            onSubmit={handleSubmit}
          >
            <div className="row">
              <div className="col-12">
                <ol className="py-4 gap-y-4">
                  <li className="mb-4">
                    <label htmlFor="importType" className=" d-block">
                      Data to be managed in bulk:
                    </label>
                    <Select
                      id="importType"
                      name="importType"
                      options={importTypes}
                      className="mb-0"
                    />
                  </li>

                  <li className="mb-4">
                    <Field name="importType">
                      {({ form }) => (
                        <label htmlFor="importType" className=" d-block">
                          Which CSV file are you uploading? Example file
                          available{" "}
                          <a
                            href={
                              form.values.importType === "user"
                                ? "/exampleFiles/user_import_example.csv"
                                : "/exampleFiles/babbage_account_import_example.csv"
                            }
                            target="_blank"
                            rel="noopener noreferrer"
                            className="underline text-brand-primary-400"
                            download
                          >
                            here
                          </a>
                          .
                        </label>
                      )}
                    </Field>

                    <Field name="file">
                      {({ field, form }) => {
                        let error = getIn(form.errors, field.name);

                        return (
                          <div className="mb-4 d-flex flex-column align-items-start">
                            <div className="overflow-hidden position-relative">
                              <input
                                style={{
                                  position: "absolute",
                                  top: 0,
                                  bottom: 0,
                                  left: 0,
                                  right: 0,
                                  opacity: 0,
                                  cursor: "pointer",
                                }}
                                name={field.name}
                                required
                                onChange={(e) => {
                                  if (e?.currentTarget?.files) {
                                    setSelectedFile(
                                      e.currentTarget.files[0].name
                                    );
                                    form.setFieldValue(
                                      field.name,
                                      e.currentTarget.files[0]
                                    );
                                  }
                                }}
                                type="file"
                                id="fileUpload"
                                accept=".csv"
                              />
                              <div className="d-flex align-items-center">
                                <button className="mr-3 btn btn-outline-primary">
                                  <FontAwesomeIcon
                                    icon={faFileCsv}
                                    className="mr-2"
                                  />
                                  Choose File
                                </button>

                                <span>
                                  {selectedFile || "No File Selected"}
                                </span>
                              </div>
                            </div>

                            {error && isString(error) && (
                              <div className="mb-3">
                                <Error error={error} name={field.name} />
                              </div>
                            )}
                          </div>
                        );
                      }}
                    </Field>
                  </li>

                  <li className="mb-4">
                    <button
                      type="submit"
                      className="btn btn-primary"
                      disabled={isSubmitting}
                    >
                      <FontAwesomeIcon icon={faUpload} className="mr-2" />
                      Upload File
                    </button>

                    {missingProperties && (
                      <p className="mt-3 text-danger">
                        Missing properties: {missingProperties}
                      </p>
                    )}

                    {unrecognisedColumns && (
                      <p className="mt-3 text-danger">
                        Unrecognised columns: {unrecognisedColumns}
                      </p>
                    )}
                  </li>
                </ol>
              </div>
            </div>
          </Form>
        </Group>

        <div className="mb-3 d-flex justify-content-center">
          <button
            className="btn btn-link"
            onClick={() => setShowImportHistory(!showImportHistory)}
          >
            <FontAwesomeIcon
              icon={showImportHistory ? faTimes : faHistory}
              className="mr-2"
            />
            {showImportHistory ? "Hide" : "Show"} Bulk Management History
          </button>
        </div>

        <ImportHistory showImportHistory={showImportHistory} />
      </div>
    </>
  );
};

const ImportHistory = ({ showImportHistory }) => {
  return useMemo(
    () => (
      <>
        {showImportHistory && (
          <Group
            key="import-history"
            id="import-history"
            name="Bulk Management History"
          >
            <UserImportList />
            <></>
          </Group>
        )}
      </>
    ),
    [showImportHistory]
  );
};

export { UserImport };
